/**
 * The clock an organisation's sales are counted on: the zone of the country it trades in (Paul,
 * 1 Nov 2026: "it will not always be SAST"). Stored as an IANA name in `settings.timeZone`; an
 * organisation without one (Flash, and everything before the setting existed) is South Africa.
 * Plain Intl, no moment, so the backend can mirror it.
 */
export const DEFAULT_ORG_TIME_ZONE = 'Africa/Johannesburg';

/** Whether `name` is a zone this runtime knows. */
export function isTimeZone(name: unknown): name is string {
    if (typeof name !== 'string' || !name) return false;
    try {
        new Intl.DateTimeFormat('en-GB', { timeZone: name });
        return true;
    } catch {
        return false;
    }
}

/** The organisation's zone, or South Africa when it names none (or names one that does not exist). */
export function orgTimeZone(settings: { timeZone?: string } | null | undefined): string {
    return isTimeZone(settings?.timeZone) ? settings!.timeZone! : DEFAULT_ORG_TIME_ZONE;
}

/** The zone's lead on UTC at `at`, as moment's `utcOffset()` takes it ("+02:00", "-05:00"). */
export function utcOffsetFor(timeZone: string, at: Date): string {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone,
        hourCycle: 'h23',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
    }).formatToParts(at);
    const n = (type: string) => Number(parts.find((p) => p.type === type)?.value || 0);
    const wall = Date.UTC(n('year'), n('month') - 1, n('day'), n('hour'), n('minute'), n('second'));
    const minutes = Math.round((wall - Math.floor(at.getTime() / 1000) * 1000) / 60_000);
    const sign = minutes < 0 ? '-' : '+';
    const abs = Math.abs(minutes);
    return `${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}
